import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { AuditLog, AuditFilters, AuditStats } from '@/types/audit';
import { startOfDay, endOfDay } from 'date-fns';

export function useAuditReports(filters: AuditFilters = {}, enabled = true) {
  return useQuery({
    queryKey: ['audit-reports', filters],
    queryFn: async () => {
      let query = supabase
        .from('audit_logs')
        .select('*')
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters.startDate) {
        query = query.gte('created_at', startOfDay(filters.startDate).toISOString());
      }
      if (filters.endDate) {
        query = query.lte('created_at', endOfDay(filters.endDate).toISOString());
      }
      if (filters.actions && filters.actions.length > 0) {
        query = query.in('action', filters.actions);
      }
      if (filters.actorUserId) {
        query = query.eq('actor_user_id', filters.actorUserId);
      }
      if (filters.companyId) {
        query = query.eq('company_id', filters.companyId);
      }
      if (filters.entityType) {
        query = query.eq('entity_type', filters.entityType);
      }

      const { data, error } = await query;

      if (error) throw error;

      return (data || []) as AuditLog[];
    },
    enabled,
  });
}

export function useAuditStats(filters: AuditFilters = {}) {
  return useQuery({
    queryKey: ['audit-stats', filters],
    queryFn: async () => {
      const { data: logs } = await supabase
        .from('audit_logs')
        .select('*')
        .order('created_at', { ascending: false });

      if (!logs) return null;

      // Calculate stats
      const totalActions = logs.length;
      const uniqueActors = new Set(logs.map(l => l.actor_user_id).filter(Boolean)).size;
      const uniqueCompanies = new Set(logs.map(l => l.company_id).filter(Boolean)).size;

      // Actions by day (last 30 days)
      const actionsByDay: Record<string, number> = {};
      logs.forEach(log => {
        const date = new Date(log.created_at).toISOString().split('T')[0];
        actionsByDay[date] = (actionsByDay[date] || 0) + 1;
      });

      // Top actions
      const actionCounts: Record<string, number> = {};
      logs.forEach(log => {
        actionCounts[log.action] = (actionCounts[log.action] || 0) + 1;
      });
      const topActions = Object.entries(actionCounts)
        .map(([action, count]) => ({ action, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      // Top actors
      const actorCounts: Record<string, number> = {};
      logs.forEach(log => {
        if (log.actor_user_id) {
          actorCounts[log.actor_user_id] = (actorCounts[log.actor_user_id] || 0) + 1;
        }
      });
      const topActors = Object.entries(actorCounts)
        .map(([user_id, count]) => ({ user_id, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      return {
        totalActions,
        uniqueActors,
        uniqueCompanies,
        actionsByDay: Object.entries(actionsByDay).map(([date, count]) => ({ date, count })),
        topActions,
        topActors,
      } as AuditStats;
    },
  });
}
